import React, { useState, useEffect } from 'react';
import { Button, Form, Input, TextArea, Message, Dropdown, Label, Loader } from 'semantic-ui-react';
import { Card } from 'common/components/Card';
import { Caption, PageSection } from 'common/styles/page';
import { authenticatedFetch } from 'common/utils/authFetch';
import { useSendTestEmailMutation } from '../../../../store/generated/admin/messaging-monitoring';

interface TestEmailForm {
  email: string;
  subject: string;
  message: string;
}

interface TestTemplateEmailForm {
  email: string;
  template_id: string;
  template_variables: string;
}

interface TemplateDetails {
  template_id: string;
  name: string;
  subject: string;
  html_body: string;
  text_body: string;
  environment_used: string;
}

interface TemplateInfo {
  template_info: TemplateDetails;
  suggested_variables: string[];
  example_variables: Record<string, string>;
}

const TestEmailSender: React.FC = () => {
  const [emailMode, setEmailMode] = useState<'simple' | 'template'>('simple');
  
  // Simple email form data
  const [simpleFormData, setSimpleFormData] = useState<TestEmailForm>({
    email: '',
    subject: 'Test Email from nuLogic Portal',
    message: 'This is a test email sent from the nuLogic Portal messaging monitoring system.'
  });
  
  // Template email form data
  const [templateFormData, setTemplateFormData] = useState<TestTemplateEmailForm>({
    email: '',
    template_id: '',
    template_variables: '{}'
  });
  
  const [errors, setErrors] = useState<Partial<TestEmailForm & TestTemplateEmailForm & { general?: string }>>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [templateInfo, setTemplateInfo] = useState<TemplateInfo | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string>('');
  const [templateOptions, setTemplateOptions] = useState<Array<{key: string, text: string, value: string}>>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [sendTestEmail, { isLoading: isSimpleLoading, error: simpleError }] = useSendTestEmailMutation();

  const validateSimpleForm = (): boolean => {
    const newErrors: Partial<TestEmailForm> = {};

    if (!simpleFormData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(simpleFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (simpleFormData.subject && simpleFormData.subject.trim().length === 0) {
      newErrors.subject = 'Subject cannot be empty if provided';
    }

    if (simpleFormData.message && simpleFormData.message.trim().length === 0) {
      newErrors.message = 'Message cannot be empty if provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTemplateForm = (): boolean => {
    const newErrors: Partial<TestTemplateEmailForm> = {};

    if (!templateFormData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(templateFormData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!templateFormData.template_id.trim()) {
      newErrors.template_id = 'Template ID is required';
    }

    // Validate JSON for template variables
    if (templateFormData.template_variables.trim()) {
      try {
        JSON.parse(templateFormData.template_variables);
      } catch (e) {
        newErrors.template_variables = 'Template variables must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSimpleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateSimpleForm()) {
      return;
    }

    try {
      await sendTestEmail({
        email: simpleFormData.email.trim(),
        subject: simpleFormData.subject.trim(),
        message: simpleFormData.message.trim()
      }).unwrap();

      setSuccessMessage(`Test email sent successfully to ${simpleFormData.email}`);
      setSimpleFormData({
        email: '',
        subject: 'Test Email from nuLogic Portal',
        message: 'This is a test email sent from the nuLogic Portal messaging monitoring system.'
      });
      setErrors({});
    } catch (err) {
      console.error('Failed to send test email:', err);
    }
  };

  const handleTemplateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateTemplateForm()) {
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    setErrors({});

    try {
      // Parse template variables
      let templateVariables = {};
      if (templateFormData.template_variables.trim()) {
        templateVariables = JSON.parse(templateFormData.template_variables);
      }

      // Use the configured API base URL instead of relative URL
      const apiBaseUrl = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;
      const response = await authenticatedFetch(`${apiBaseUrl}/api/admin/v1/messaging-monitoring/send_test_template_email/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: templateFormData.email.trim(),
          template_id: templateFormData.template_id.trim(),
          template_variables: templateVariables
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(`Test template email sent successfully to ${templateFormData.email} using template ${templateFormData.template_id}`);
        setTemplateFormData({
          email: '',
          template_id: '',
          template_variables: '{}'
        });
      } else {
        setErrors({ 
          general: data.error || 'Failed to send test template email' 
        });
      }
    } catch (err) {
      console.error('Failed to send test template email:', err);
      setErrors({ 
        general: 'Network error. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSimpleInputChange = (field: keyof TestEmailForm, value: string) => {
    setSimpleFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleTemplateInputChange = (field: keyof TestTemplateEmailForm, value: string) => {
    setTemplateFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const fetchTemplateDetails = async (templateId: string) => {
    if (!templateId) {
      setTemplateInfo(null);
      setTemplateError('');
      return;
    }

    setLoadingTemplate(true);
    setTemplateError('');

    try {
      const response = await authenticatedFetch(`/api/admin/v1/messaging-monitoring/get_template_details/?template_id=${encodeURIComponent(templateId)}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok) {
        setTemplateInfo(data);
        
        // Prepopulate template variables with examples
        if (data.example_variables && Object.keys(data.example_variables).length > 0) {
          setTemplateFormData(prev => ({
            ...prev,
            template_variables: JSON.stringify(data.example_variables, null, 2)
          }));
        }
      } else {
        setTemplateError(data.error || 'Failed to fetch template details');
        setTemplateInfo(null);
      }
    } catch (err) {
      console.error('Failed to fetch template details:', err);
      setTemplateError('Network error. Please try again.');
      setTemplateInfo(null);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleTemplateChange = (e: React.SyntheticEvent, data: any) => {
    const value = data.value as string;
    handleTemplateInputChange('template_id', value);
    fetchTemplateDetails(value);
  };

  const fetchTemplates = async () => {
    if (templateOptions.length > 0) return; // Already loaded
    
    setLoadingTemplates(true);
    try {
      // Use the configured API base URL instead of relative URL
      const apiBaseUrl = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;
      const response = await authenticatedFetch(`${apiBaseUrl}/api/admin/v1/messaging-monitoring/list_templates/`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const options = data.templates
          .filter((template: any) => template.template_id) // Only show templates with valid IDs
          .map((template: any) => ({
            key: template.template_id,
            text: template.name || template.template_id,
            value: template.template_id
          }));
        setTemplateOptions(options);
      } else {
        console.error('Failed to fetch templates:', data.error);
        // Fallback to known working templates
        setTemplateOptions([
          { key: 'password-reset', text: 'Password Reset Email', value: 'password-reset' },
          { key: 'user-invite', text: 'User Invite Emails', value: 'user-invite' },
          { key: 'trial-invite', text: 'Trial Invite Emails', value: 'trial-invite' },
          { key: 'team-tool', text: 'Team Tool', value: 'team-tool' },
          { key: 'personality-survey-email', text: 'Personality Survey Email', value: 'personality-survey-email' },
          { key: 'brief-survey', text: 'Brief Survey', value: 'brief-survey' }
        ]);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      // Fallback to known working templates
      setTemplateOptions([
        { key: 'password-reset', text: 'Password Reset Email', value: 'password-reset' },
        { key: 'user-invite', text: 'User Invite Emails', value: 'user-invite' },
        { key: 'trial-invite', text: 'Trial Invite Emails', value: 'trial-invite' },
        { key: 'team-tool', text: 'Team Tool', value: 'team-tool' },
        { key: 'personality-survey-email', text: 'Personality Survey Email', value: 'personality-survey-email' },
        { key: 'brief-survey', text: 'Brief Survey', value: 'brief-survey' }
      ]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleModeChange = (mode: 'simple' | 'template') => {
    setEmailMode(mode);
    setErrors({});
    setSuccessMessage('');
    setTemplateInfo(null);
    setTemplateError('');
    
    // Fetch templates when switching to template mode
    if (mode === 'template') {
      fetchTemplates();
    }
  };

  // Fetch templates on component mount
  useEffect(() => {
    if (emailMode === 'template') {
      fetchTemplates();
    }
  }, [emailMode]);

  return (
    <PageSection padtop>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <Card>
          <h2 style={{ margin: '0 0 8px 0' }}>Send Test Email</h2>
          <Caption>
            Send a test email to verify the messaging system function.
          </Caption>

          {/* Mode Selection */}
          <div style={{ marginBottom: '20px',  marginTop: '10px' }}>
            <Button.Group>
              <Button 
                active={emailMode === 'simple'} 
                onClick={() => handleModeChange('simple')}
              >
                Simple Email
              </Button>
              <Button 
                active={emailMode === 'template'} 
                onClick={() => handleModeChange('template')}
              >
                Template Email
              </Button>
            </Button.Group>
          </div>

          {/* Simple Email Form */}
          {emailMode === 'simple' && (
            <Form onSubmit={handleSimpleSubmit}>
              {/* Email Address */}
              <Form.Field required>
                <label>Email Address</label>
                <Input
                  type="email"
                  value={simpleFormData.email}
                  onChange={(e) => handleSimpleInputChange('email', e.target.value)}
                  placeholder="recipient@example.com"
                  error={!!errors.email}
                />
                {errors.email && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.email}
                  </div>
                )}
              </Form.Field>

              {/* Subject */}
              <Form.Field>
                <label>Subject Line</label>
                <Input
                  type="text"
                  value={simpleFormData.subject}
                  onChange={(e) => handleSimpleInputChange('subject', e.target.value)}
                  placeholder="Test Email from nuLogic Portal"
                  error={!!errors.subject}
                />
                {errors.subject && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.subject}
                  </div>
                )}
              </Form.Field>

              {/* Message */}
              <Form.Field>
                <label>Message Content</label>
                <TextArea
                  value={simpleFormData.message}
                  onChange={(e) => handleSimpleInputChange('message', e.target.value)}
                  placeholder="Enter your test message here..."
                  rows={4}
                  error={!!errors.message}
                />
                {errors.message && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.message}
                  </div>
                )}
              </Form.Field>

              {/* Submit Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <Button
                  type="submit"
                  disabled={isSimpleLoading}
                  loading={isSimpleLoading}
                  primary
                  style={{ minWidth: '140px' }}
                >
                  Send Test Email
                </Button>
              </div>
            </Form>
          )}

          {/* Template Email Form */}
          {emailMode === 'template' && (
            <Form onSubmit={handleTemplateSubmit}>
              {/* Email Address */}
              <Form.Field required>
                <label>Email Address</label>
                <Input
                  type="email"
                  value={templateFormData.email}
                  onChange={(e) => handleTemplateInputChange('email', e.target.value)}
                  placeholder="recipient@example.com"
                  error={!!errors.email}
                />
                {errors.email && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.email}
                  </div>
                )}
              </Form.Field>

              {/* Template Selection */}
              <Form.Field required>
                <label>Template</label>
                <Dropdown
                  selection
                  search
                  placeholder={loadingTemplates ? "Loading templates..." : "Select a template..."}
                  value={templateFormData.template_id}
                  onChange={handleTemplateChange}
                  options={templateOptions}
                  error={!!errors.template_id}
                  loading={loadingTemplate || loadingTemplates}
                  disabled={loadingTemplates}
                />
                {loadingTemplates && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Loading available templates from Postmark...
                  </div>
                )}
                {!loadingTemplates && templateOptions.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Showing {templateOptions.length} available Standard template(s) from Postmark. Only Standard templates can be used for sending emails.
                  </div>
                )}
                {errors.template_id && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.template_id}
                  </div>
                )}
              </Form.Field>

              {/* Template Information */}
              {templateInfo && (
                <Form.Field>
                  <label>Template Information</label>
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #e9ecef', 
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Name:</strong> {templateInfo.template_info.name}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Subject:</strong> {templateInfo.template_info.subject}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Environment:</strong> {templateInfo.template_info.environment_used}
                    </div>
                    {templateInfo.suggested_variables.length > 0 && (
                      <div>
                        <strong>Detected Variables:</strong> {templateInfo.suggested_variables.join(', ')}
                      </div>
                    )}
                  </div>
                </Form.Field>
              )}

              {/* Template Error */}
              {templateError && (
                <Message negative>
                  <Message.Header>Template Error</Message.Header>
                  <p>{templateError}</p>
                </Message>
              )}

              {/* Template Variables */}
              <Form.Field>
                <label>
                  Template Variables (JSON)
                  {templateInfo && templateInfo.suggested_variables.length > 0 && (
                    <Label color="blue" size="mini" style={{ marginLeft: '8px' }}>
                      {templateInfo.suggested_variables.length} variables detected
                    </Label>
                  )}
                </label>
                <TextArea
                  value={templateFormData.template_variables}
                  onChange={(e) => handleTemplateInputChange('template_variables', e.target.value)}
                  placeholder='{"name": "John Doe", "company": "Example Corp"}'
                  rows={6}
                  error={!!errors.template_variables}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  {templateInfo && templateInfo.suggested_variables.length > 0 ? (
                    <>
                      <strong>Detected variables:</strong> {templateInfo.suggested_variables.join(', ')}<br/>
                      <strong>Example values have been pre-filled above.</strong> Modify as needed.
                    </>
                  ) : (
                    <>
                      Enter template variables as JSON. Example: {`{"name": "John Doe", "company": "Example Corp"}`}
                    </>
                  )}
                </div>
                {errors.template_variables && (
                  <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                    {errors.template_variables}
                  </div>
                )}
              </Form.Field>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <div>
                  {templateInfo && templateInfo.example_variables && Object.keys(templateInfo.example_variables).length > 0 && (
                    <Button
                      type="button"
                      onClick={() => {
                        setTemplateFormData(prev => ({
                          ...prev,
                          template_variables: JSON.stringify(templateInfo.example_variables, null, 2)
                        }));
                      }}
                      size="small"
                      style={{ marginRight: '8px' }}
                    >
                      Reset to Examples
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={() => {
                      setTemplateFormData(prev => ({
                        ...prev,
                        template_variables: '{}'
                      }));
                    }}
                    size="small"
                  >
                    Clear Variables
                  </Button>
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  loading={isLoading}
                  primary
                  style={{ minWidth: '180px' }}
                >
                  Send Test Template Email
                </Button>
              </div>
            </Form>
          )}

          {/* Success Message */}
          {successMessage && (
            <Message positive style={{ marginTop: '16px' }}>
              <Message.Header>Test Email Sent Successfully</Message.Header>
              <p>{successMessage}</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                <strong>Note:</strong> The email will appear as "In Flight" in the monitoring dashboard until status feedback is received.
              </p>
            </Message>
          )}

          {/* Error Messages */}
          {simpleError && (
            <Message negative style={{ marginTop: '16px' }}>
              Failed to send test email. Please try again.
            </Message>
          )}
          
          {errors.general && (
            <Message negative style={{ marginTop: '16px' }}>
              <Message.Header>Error</Message.Header>
              <p>{errors.general}</p>
            </Message>
          )}
        </Card>
      </div>
    </PageSection>
  );
};

export default TestEmailSender;
